import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { InlineEditActions } from './slice'
import { postInlineEdit } from '../fetch/post-inline-edit'
import { InlineEditPayload } from './schemas'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationLongMs } from '@/constants/constants-ui'

export const inlineEditListenerMiddleware = createListenerMiddleware()
const startListener = inlineEditListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

/**
 * Submit an inline edit request via HTTP. Supports cancellation via cancelInlineEdit action.
 */
startListener({
  actionCreator: InlineEditActions.submitInlineEdit,
  effect: async (action, listenerApi) => {
    const { conversationId, artifactVersionId, editInstruction, caselawSearchFilter, lrrSearchFilter, userFilesRag, filePaths } = action.payload

    // Get the current selection anchors
    const session = listenerApi.getState().inlineEditState.sessions[conversationId]
    if (!session?.selection) {
      console.error('No inline edit selection found for conversation:', conversationId)
      listenerApi.dispatch(InlineEditActions.setSubmitting({ conversationId, isSubmitting: false }))
      return
    }

    const { anchorStart, anchorEnd } = session.selection

    // Set submitting state
    listenerApi.dispatch(InlineEditActions.setSubmitting({ conversationId, isSubmitting: true }))

    // Build the payload. postInlineEdit owns request-shape normalization.
    const payload: InlineEditPayload = {
      artifact_version_id: artifactVersionId,
      edit_instruction: editInstruction,
      anchor_start: anchorStart,
      anchor_end: anchorEnd,
      caselaw_search_filter: caselawSearchFilter,
      lrr_search_filter: lrrSearchFilter,
      user_files_rag: userFilesRag ?? null,
      file_paths: filePaths ?? [],
    }

    // Set up abort controller for cancellation
    const abortController = new AbortController()

    // Fork a task that waits for the cancel action and aborts the fetch
    const cancelTask = listenerApi.fork(async () => {
      await listenerApi.condition(
        (action) => InlineEditActions.cancelInlineEdit.match(action) && (action.payload as { conversationId: string }).conversationId === conversationId
      )
      abortController.abort()
    })

    try {
      const result = await postInlineEdit(conversationId, payload, abortController.signal)

      // Store the replacement content and references — the listener in agent/conversations handles upserting references
      listenerApi.dispatch(
        InlineEditActions.setPendingReplacement({
          conversationId,
          replacement: { content: result.content, references: result.references },
        })
      )
    } catch (error) {
      if (abortController.signal.aborted) {
        // User cancelled — no error toast needed
        return
      }
      console.error('Inline edit failed:', error)
      const message = error instanceof Error ? error.message : 'Failed to apply edit. Please try again.'
      listenerApi.dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message,
          durationMs: kToastDurationLongMs,
        })
      )
    } finally {
      cancelTask.cancel()
      listenerApi.dispatch(InlineEditActions.setSubmitting({ conversationId, isSubmitting: false }))
    }
  },
})
